/**
 * BurnRatePanel — View C (RAISE-16867 D2/D8).
 *
 * Renders KPI mini-row (MTD, Projected EOM, Avg Daily 7d) + recharts
 * ComposedChart with daily/monthly toggle. Uses window-level blended rates
 * to avoid the Opus DEFAULT_PRICING overestimate (D2).
 */

import { useState, useMemo } from "react";
import {
  Bar,
  ComposedChart,
  Line,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useTokenCost } from "@/hooks/useTokenCost";
import { sinceDate } from "@/components/tokens/PeriodFilter";
import {
  blendedRates,
  toDailyCosts,
  toMonthlyCosts,
  projectEndOfMonth,
  type DailyCost,
} from "@/lib/cost/burnRate";
import { formatCost } from "@/lib/format";

// ---------------------------------------------------------------------------
// KPI card (inline — simple enough not to warrant a separate component here)
// ---------------------------------------------------------------------------

interface KpiMiniProps {
  label: string;
  value: string;
}

function KpiMini({ label, value }: KpiMiniProps) {
  return (
    <div className="rounded-lg border p-4">
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <p className="mt-1 text-xl font-bold">{value}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Toggle buttons (daily / monthly)
// ---------------------------------------------------------------------------

type ChartView = "daily" | "monthly";

interface ViewToggleProps {
  value: ChartView;
  onChange: (v: ChartView) => void;
  labelDaily: string;
  labelMonthly: string;
}

function ViewToggle({ value, onChange, labelDaily, labelMonthly }: ViewToggleProps) {
  return (
    <div className="flex gap-1 rounded-md border p-0.5 text-sm">
      <button
        type="button"
        onClick={() => onChange("daily")}
        className={`rounded px-3 py-1 transition-colors ${
          value === "daily"
            ? "bg-primary text-primary-foreground"
            : "text-muted-foreground hover:text-foreground"
        }`}
      >
        {labelDaily}
      </button>
      <button
        type="button"
        onClick={() => onChange("monthly")}
        className={`rounded px-3 py-1 transition-colors ${
          value === "monthly"
            ? "bg-primary text-primary-foreground"
            : "text-muted-foreground hover:text-foreground"
        }`}
      >
        {labelMonthly}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export interface BurnRatePanelProps {
  periodDays: number;
}

export function BurnRatePanel({ periodDays }: BurnRatePanelProps) {
  const { t } = useTranslation();
  const [chartView, setChartView] = useState<ChartView>("daily");

  const since = sinceDate(periodDays);
  // Limit 1825 (5 years of daily rows) — prevents silent blank from the
  // server's default limit=100 on "All time" or multi-year views (S1).
  const filters = since ? { since, limit: 1825 } : { limit: 1825 };

  // Two parallel requests: by date (for daily bars) and by model (for blended rates)
  const { items: dateItems, loading: loadingDate, error: errorDate } = useTokenCost(null, "date", filters);
  const { items: modelItems, error: errorModel } = useTokenCost(null, "model", filters);

  const { dailyCosts, projection, periodSpend } = useMemo(() => {
    const rates = blendedRates(modelItems);
    if (!rates) return { dailyCosts: [], projection: null, periodSpend: 0 };
    const dc = toDailyCosts(dateItems, rates);
    const todayIso = new Date().toISOString().slice(0, 10);
    const proj = projectEndOfMonth(dc, todayIso);
    const spend = dc.reduce((acc, d) => acc + d.costUsd, 0);
    return { dailyCosts: dc, projection: proj, periodSpend: spend };
  }, [dateItems, modelItems]);

  const chartData = useMemo(() => {
    if (chartView === "monthly") {
      return toMonthlyCosts(dailyCosts).map((m) => ({
        label: m.month,
        cost: m.costUsd,
        projected: undefined as number | undefined,
      }));
    }
    // Daily + dashed projection line from today to EOM
    const todayIso = new Date().toISOString().slice(0, 10);
    const byDate = new Map<string, DailyCost>(dailyCosts.map((d) => [d.date, d]));
    const result: Array<{ label: string; cost?: number; projected?: number }> = [];

    for (const d of dailyCosts) {
      result.push({ label: d.date, cost: d.costUsd });
    }

    if (projection && projection.remainingDays > 0 && projection.avgDaily7 > 0) {
      const todayDate = new Date(todayIso + "T00:00:00Z");
      for (let i = 1; i <= projection.remainingDays; i++) {
        const futureDate = new Date(todayDate.getTime() + i * 86_400_000);
        const futureIso = futureDate.toISOString().slice(0, 10);
        if (!byDate.has(futureIso)) {
          result.push({ label: futureIso, projected: projection.avgDaily7 });
        }
      }
    }

    return result;
  }, [dailyCosts, chartView, projection]);

  const hasData = dailyCosts.length > 0;
  const apiError = errorDate ?? errorModel;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold">
            {t("costPage.burnRate.title")}
          </CardTitle>
          <ViewToggle
            value={chartView}
            onChange={setChartView}
            labelDaily={t("costPage.burnRate.toggleDaily")}
            labelMonthly={t("costPage.burnRate.toggleMonthly")}
          />
        </div>
      </CardHeader>
      <CardContent>
        {apiError ? (
          <p className="text-sm text-destructive">{apiError}</p>
        ) : loadingDate ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : !hasData ? (
          <p className="text-sm text-muted-foreground">
            {t("costPage.burnRate.noData")}
          </p>
        ) : (
          <>
            {/* KPI mini-row */}
            <div className="mb-6 grid grid-cols-3 gap-3">
              <KpiMini
                label={
                  periodDays >= 28
                    ? t("costPage.burnRate.mtd")
                    : t("costPage.burnRate.periodSpend")
                }
                value={
                  periodDays >= 28
                    ? (projection ? formatCost(projection.mtd) : "—")
                    : formatCost(periodSpend)
                }
              />
              <KpiMini
                label={t("costPage.burnRate.projectedEom")}
                value={projection ? formatCost(projection.projectedEom) : "—"}
              />
              <KpiMini
                label={t("costPage.burnRate.avgDaily7")}
                value={projection ? formatCost(projection.avgDaily7) : "—"}
              />
            </div>

            {/* Chart */}
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 10 }}
                    tickLine={false}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    tick={{ fontSize: 10 }}
                    tickLine={false}
                    tickFormatter={(v: number) => formatCost(v)}
                  />
                  <Tooltip
                    formatter={(v: number) => [
                      formatCost(v),
                      t("costPage.burnRate.estLabel"),
                    ]}
                  />
                  <Bar dataKey="cost" fill="hsl(var(--primary))" radius={2} />
                  <Line
                    type="monotone"
                    dataKey="projected"
                    stroke="hsl(var(--muted-foreground))"
                    strokeDasharray="4 2"
                    dot={false}
                    connectNulls={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
